import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Modal from '../components/Modal';

const ConfirmContext = createContext(null);

/**
 * Promise-based confirmation, so a destructive handler reads as a straight line:
 *
 *   if (!(await confirm({ ... }))) return;
 *   await remove(id);
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);

  const confirm = useCallback((options) => {
    setState({
      title: 'Are you sure?',
      message: '',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      tone: 'danger',
      ...options,
    });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result) => {
    if (resolver.current) resolver.current(result);
    resolver.current = null;
    setState(null);
    setBusy(false);
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}

      {state && (
        <Modal
          open
          onClose={() => settle(false)}
          kicker={state.kicker}
          title={state.title}
          footer={
            <>
              <button type="button" className="btn-ghost" onClick={() => settle(false)} disabled={busy}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className="btn-brand"
                style={
                  state.tone === 'danger'
                    ? { background: 'linear-gradient(120deg, #c62828, #e05252)', boxShadow: '0 6px 14px rgba(198,40,40,.34)' }
                    : undefined
                }
                onClick={() => {
                  setBusy(true);
                  settle(true);
                }}
                disabled={busy}
              >
                {busy && <span className="spinner" />}
                {state.confirmLabel}
              </button>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>{state.message}</p>
          {state.detail && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--ink-muted)' }}>{state.detail}</p>
          )}
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside a ConfirmProvider');
  return ctx.confirm;
}
